import type { QueryRowType } from "@rocicorp/zero";
import { useQuery, useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import {
  type CSSProperties,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { sharedMutators } from "../../../shared/mutators";
import { queries } from "../../../shared/queries";
import { useDeadlinePassed } from "../../hooks/useDeadlinePassed";
import { useFormatRelativeTime } from "../../hooks/useFormatRelativeTime";
import { useHasAccess } from "../../hooks/useHasAccess";
import { uniqueByType } from "../../lib/connector";
import { navigate, routes, useCurrentUrl } from "../../lib/urls";
import { useMonitoringAnalytics, useMonitoringInteraction } from "./analytics";
import { playClick } from "../../lib/sounds";
import { Button } from "../Button";
import { ClickButton } from "../ClickButton";
import { ConnectorAvatars } from "../ConnectorAvatars";
import { Icon } from "../Icon";
import { INFO_ICON_IDLE_OPACITY } from "../InfoIcon";
import { Modal } from "../Modal";
import { RefreshSpin } from "../RefreshSpin";
import { SoftButton } from "../SoftButton";
import { SpaceBadge } from "../SpaceBadge";
import { Text } from "../Text";
import { Tooltip } from "../Tooltip";
import { WorkflowBadge } from "../WorkflowBadge";
import {
  pairKey,
  pendingDuplicatePairs,
} from "../../../shared/duplicateWorkflows";
import {
  DETAIL_MODAL_STYLE,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalHeaderRight,
  ModalTitle,
  SummaryCard,
} from "./MonitoringDetailCard";
import { PanelSkeleton } from "./workflowsLayout";

type PairRow = QueryRowType<ReturnType<typeof queries.monitorDuplicatePairs>>;
type WorkflowSide = NonNullable<PairRow["workflowA"]>;
// A pair whose two workflow rows have both synced; only these are reviewable.
type ReadyPair = PairRow & {
  workflowA: WorkflowSide;
  workflowB: WorkflowSide;
};

const CARD_WIDTH = 280;
const CARD_HEIGHT = 320;
const FAN_X = 134;
const SIDE_X = 148;
const SKIP_OUT_X = 380;
const OFF_X = 700;
const DEAL_MS = 480;
const SKIP_STAGE_MS = 240;
// A run in flight stores only its fresh startedAt (completedAt null); bound the
// "Checking…" state so a stalled or capped run eventually re-enables Recheck
// rather than reading as checking forever.
const IN_PROGRESS_MS = 2 * 60 * 60 * 1000;

// Opacity + hover mirror the InfoIcon affordance next to the workflow menu's
// autofix tooltip.
export const RefreshButton = styled(ClickButton)`
  display: flex;
  flex-shrink: 0;
  opacity: ${INFO_ICON_IDLE_OPACITY};
  &:hover {
    opacity: 1;
  }
  &&&:disabled {
    opacity: 1;
  }
`;

const Stage = styled("div")`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 8px 4px 24px;
  text-align: center;
`;

const Prompt = styled("div")`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  margin-top: 20px;
`;

// Each card is keyed by pair and side, so shifting the deck window animates
// depth changes purely via CSS transitions.
const Deck = styled("div")`
  position: relative;
  flex: 1;
  min-height: 0;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  /* Dismissed cards fling past the edge to OFF_X; clip x so they vanish
     off-screen instead of forcing a horizontal scrollbar. */
  overflow-x: clip;

  &:has(> .selectable:hover) > .selectable:not(:hover) {
    filter: grayscale(1);
  }
`;

const Card = styled("div")`
  position: absolute;
  top: 50%;
  left: 50%;
  width: ${CARD_WIDTH}px;
  height: ${CARD_HEIGHT}px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 36px 20px 20px;
  border-radius: 16px;
  background: var(--surface-primary);
  outline: 0.5px solid var(--border-alpha);
  box-shadow: var(--elevation-low);
  text-align: center;
  will-change: transform, opacity;
  transition:
    transform ${DEAL_MS}ms cubic-bezier(0.2, 0.72, 0.2, 1),
    opacity 360ms ease,
    box-shadow 160ms ease,
    outline-color 160ms ease,
    filter 200ms ease;

  & > * {
    transition:
      opacity 200ms ease,
      filter 200ms ease;
  }

  /* Fade only the content of the receded stack behind the active pair; the
     card surface stays opaque, so target the children, not the card itself. */
  &.receded > * {
    opacity: 0.2;
    filter: grayscale(1);
  }

  &.selectable {
    cursor: pointer;
  }

  &.selectable:hover {
    outline-color: var(--border-strong);
    box-shadow: var(--elevation-high);
  }
`;

const BadgeWrap = styled("div")`
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;

  & > * {
    transform: scale(1.6);
  }
`;

const CardName = styled("div")`
  min-width: 0;
  max-width: 100%;
`;

const Meta = styled("div")`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

const Purpose = styled(Text)`
  width: 100%;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

// Flexes to fill the rest of the card so cards stay balanced regardless of
// description length.
const About = styled("div")`
  width: 100%;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 12px;
  border-radius: 12px;
  background: var(--surface-secondary);
`;

const Decisions = styled("div")`
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-shrink: 0;
`;

const VerdictButton = styled(Button)`
  width: 88px;
  justify-content: center;
`;

const ProgressTrack = styled("div")`
  width: 100%;
  height: 4px;
  border-radius: 999px;
  background: var(--alpha8);
  overflow: hidden;
  flex-shrink: 0;
`;

const ProgressFill = styled("div")`
  height: 100%;
  border-radius: 999px;
  background: var(--text-primary);
  transition: width 200ms ease;
`;

const FooterLeft = styled("div")`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const DoneState = styled("div")`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  text-align: center;
`;

const DoneIcon = styled("div")`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  color: var(--green-600);
  background: var(--green-100);
`;

// The mini cards deliberately bleed off the rollup card's edges; pointer
// events are off so the whole card stays a single click target.
const MiniDeck = styled("div")`
  position: absolute;
  inset: 0 0 0 auto;
  width: 240px;
  pointer-events: none;
  z-index: 0;
`;

// The outer layer holds the fanned pose and the inner carries the hover shift
// (applied from SummaryCard's :hover), so the transforms compose instead of
// clobbering each other.
const MiniCardPose = styled("div")`
  position: absolute;
  top: 18px;
  width: 136px;
  height: 184px;
`;

const MiniCard = styled("div")`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 22px 16px 16px;
  border-radius: 16px;
  background: var(--surface-primary);
  outline: 0.5px solid var(--border-alpha);
  box-shadow: var(--elevation-low);
  transition: transform 260ms cubic-bezier(0.2, 0.72, 0.2, 1);
`;

const MiniBadge = styled("div")`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 44px;

  & > * {
    transform: scale(1.35);
  }
`;

// Sized to WorkflowBadge's 32px rounded-square footprint so MiniBadge's scale
// renders both identically.
const GhostBadge = styled("div")`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: var(--alpha8);
`;

const MiniName = styled("div")`
  width: 72%;
  height: 6px;
  border-radius: 999px;
  background: var(--alpha8);
`;

const MiniAbout = styled("div")`
  width: 100%;
  flex: 1;
  border-radius: 10px;
  background: var(--surface-secondary);
`;

function badgeProps(row: WorkflowSide) {
  return {
    id: row.id,
    badgeSeed: row.badgeSeed,
    pausedAt: row.pausedAt,
    branches: row.branches ? [row.branches] : [],
  };
}

function connectorsOf(row: WorkflowSide) {
  return uniqueByType(
    (row.branches?.headCommit?.appUsages ?? [])
      .map((usage) => usage.connectedApp)
      .filter((app) => app != null)
  );
}

type CardPoseOpts = {
  entered: boolean;
  resolving: boolean;
  backRising: boolean;
  recede: boolean;
  leaveDir: "different" | "skip";
  leaveStage: "out" | "in";
};

// Pose for one card by its depth (pos): <0 is the dismissed pair leaving (flung
// aside for "different"; thrown out then tucked under the stack for "skip"), 0
// is the active pair (fanned, or side by side while choosing), >0 waits in the
// stack. Every state emits the same translate/rotate/scale structure so
// transitions interpolate per-component; applied inline so goober's transition
// fires.
function cardPose(
  pos: number,
  side: "left" | "right",
  { entered, resolving, backRising, recede, leaveDir, leaveStage }: CardPoseOpts
): { transform: string; opacity: number } {
  const dir = side === "left" ? -1 : 1;
  let x = 0;
  let y = 0;
  let r = 0;
  let s = 1;
  let opacity = 1;
  if (pos < 0) {
    if (leaveDir === "different") {
      x = dir * OFF_X;
      r = dir * 16;
      opacity = 0;
    } else if (leaveStage === "out") {
      x = dir * SKIP_OUT_X;
      r = dir * 12;
    } else {
      y = 40;
      s = 0.85;
      opacity = 0;
    }
  } else if (pos > 0) {
    y = pos * 14;
    s = (1 - pos * 0.05) * (recede ? 0.8 : 1);
  } else if (backRising) {
    x = dir * SKIP_OUT_X;
    r = dir * 12;
  } else if (!entered) {
    y = 14;
    s = 0.95;
  } else if (resolving) {
    x = dir * SIDE_X;
  } else {
    x = dir * FAN_X;
    y = side === "left" ? 12 : -16;
    r = dir * 5;
  }
  return {
    transform: `translate(-50%, -50%) translateX(${x}px) translateY(${y}px) rotate(${r}deg) scale(${s})`,
    opacity,
  };
}

function MiniWorkflowCard({
  workflow,
  pose,
  style,
}: {
  workflow?: WorkflowSide;
  pose: "front" | "back";
  style: CSSProperties;
}) {
  return (
    <MiniCardPose style={style}>
      <MiniCard data-deck={pose}>
        <MiniBadge>
          {workflow ? (
            <WorkflowBadge workflow={badgeProps(workflow)} hover={false} />
          ) : (
            <GhostBadge />
          )}
        </MiniBadge>
        <MiniName />
        <MiniAbout />
      </MiniCard>
    </MiniCardPose>
  );
}

function PreviewDeck({ pair }: { pair?: ReadyPair }) {
  return (
    <MiniDeck aria-hidden>
      <MiniWorkflowCard
        workflow={pair?.workflowA}
        pose="back"
        style={{ right: 70, zIndex: 1, transform: "rotate(-4deg)" }}
      />
      <MiniWorkflowCard
        workflow={pair?.workflowB}
        pose="front"
        style={{
          right: -8,
          zIndex: 2,
          transform: "translateY(10px) rotate(8deg)",
        }}
      />
    </MiniDeck>
  );
}

function CardContent({
  workflow: row,
  purpose,
}: {
  workflow: WorkflowSide;
  purpose: string;
}) {
  const connectors = connectorsOf(row);
  return (
    <>
      <BadgeWrap>
        <WorkflowBadge workflow={badgeProps(row)} hover={false} />
      </BadgeWrap>
      <CardName>
        <Text variant="text-regular-md" ellipsis>
          {row.name}
        </Text>
      </CardName>
      {row.space ? <SpaceBadge space={row.space} /> : null}
      <Meta>
        {connectors.length > 0 ? (
          <ConnectorAvatars
            connected={connectors.map((app) => ({
              id: app.id,
              name: app.name,
              app,
            }))}
          />
        ) : (
          <Text variant="text-small" color="var(--text-secondary)">
            No apps
          </Text>
        )}
      </Meta>
      <About>
        <Purpose
          variant="text-small"
          color={purpose ? "var(--text-primary)" : "var(--text-secondary)"}
        >
          {purpose || "No description"}
        </Purpose>
      </About>
    </>
  );
}

export function DuplicateWorkflowsPanel() {
  const [pairRows, pairsQuery] = useQuery(queries.monitorDuplicatePairs());
  const [reviews, reviewsQuery] = useQuery(queries.monitorDuplicateReviews());
  const [detectionRun, detectionRunQuery] = useQuery(
    queries.monitorDuplicateDetectionRun()
  );
  const z = useZero();
  const monitoringAnalytics = useMonitoringAnalytics();
  const recordInteraction = useMonitoringInteraction();
  const [lastCompletedAt, setLastCompletedAt] = useState<number | null>(null);
  const { panel } = useCurrentUrl(routes.monitoring.workflows);
  const open = panel === "duplicates";
  const [prevOpen, setPrevOpen] = useState(false);
  const [queue, setQueue] = useState<ReadyPair[]>([]);
  const [index, setIndex] = useState(0);
  const [resolving, setResolving] = useState(false);
  const [leaveDir, setLeaveDir] = useState<"different" | "skip">("skip");
  const [leaveStage, setLeaveStage] = useState<"out" | "in">("in");
  const [throwing, setThrowing] = useState(false);
  const [backRising, setBackRising] = useState(false);
  const [entered, setEntered] = useState(false);
  const [animating, setAnimating] = useState(false);
  const animatingRef = useRef(false);

  const pendingPairs = useMemo(
    () =>
      pendingDuplicatePairs(pairRows ?? [], reviews ?? []).filter(
        (p): p is ReadyPair => p.workflowA != null && p.workflowB != null
      ),
    [pairRows, reviews]
  );
  const reviewedKeys = useMemo(
    () =>
      new Set(
        (reviews ?? []).map((r) => pairKey(r.workflowAId, r.workflowBId))
      ),
    [reviews]
  );

  const current = queue[index];
  // Keeping a card pauses the other workflow, which takes a live version out of
  // operation and so needs space:push_live in that workflow's space.
  const canPauseA = useHasAccess(
    "space:push_live",
    current?.workflowA.spaceId ?? ""
  );
  const canPauseB = useHasAccess(
    "space:push_live",
    current?.workflowB.spaceId ?? ""
  );
  const total = queue.length;
  // Pairs in this session's queue still awaiting a verdict — drops as pairs
  // are resolved (recorded same/different), but not when merely skipped.
  const remaining = queue.filter(
    (p) => !reviewedKeys.has(pairKey(p.workflowAId, p.workflowBId))
  ).length;
  const finished = index >= total;
  const showDone = finished && !animating;

  // Fan the active pair open on open. Two rAFs let the tucked "stack" pose
  // paint before we flip to "fanned" so the transition runs; later pairs rise
  // from the stack on their own as the window shifts, so this is open-only.
  useEffect(() => {
    if (!open) return;
    // oxlint-disable-next-line react/react-compiler -- entrance animation reset; syncs with the rAF sequence below
    setEntered(false);
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setEntered(true));
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [open]);

  const openDeck = () => {
    recordInteraction("workflows.panel_drilldown_open", {
      value: "duplicates",
    });
    navigate(routes.monitoring.workflows, { panel: "duplicates", search: "" });
  };
  const close = () =>
    navigate(
      routes.monitoring.workflows,
      { panel: "", search: "" },
      { replace: true }
    );

  const record = (decision: "same" | "different") => {
    if (!current) return;
    z.mutate(
      sharedMutators.reviewDuplicatePair({
        analytics: monitoringAnalytics(),
        workflowAId: current.workflowAId,
        workflowBId: current.workflowBId,
        decision,
      })
    );
  };

  // Shift the deck window by one. The front pair flings off (per dir) while the
  // next pair rises from the stack and fans open — all via CSS, since each pair
  // keeps its key across the depth change.
  const shift = (to: number, dir: "different" | "skip") => {
    if (animatingRef.current) return;
    animatingRef.current = true;
    setLeaveDir(dir);
    setResolving(false);
    setBackRising(false);
    setAnimating(true);
    // A forward skip throws the pair out to the sides, then (after a beat)
    // tucks it back under the stack; everything else leaves in a single move.
    if (dir === "skip" && to > index) {
      setLeaveStage("out");
      setThrowing(true);
      window.setTimeout(() => setLeaveStage("in"), SKIP_STAGE_MS);
    } else {
      setLeaveStage("in");
      setThrowing(false);
    }
    setIndex(to);
    window.setTimeout(() => {
      animatingRef.current = false;
      setAnimating(false);
      setThrowing(false);
    }, DEAL_MS);
  };

  const decide = (kind: "same" | "different" | "skip") => {
    if (animatingRef.current) return;
    recordInteraction("workflows.duplicate_decision", { value: kind });
    if (kind === "same") {
      setResolving(true);
      return;
    }
    if (kind === "different") record("different");
    shift(index + 1, kind);
  };

  // The optimistic writes run after the first animated frame so their
  // synchronous work doesn't stall the fling.
  const keep = (keepId: string) => {
    if (animatingRef.current || !current) return;
    const pauseId =
      current.workflowAId === keepId
        ? current.workflowBId
        : current.workflowAId;
    shift(index + 1, "different");
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        record("same");
        z.mutate(
          sharedMutators.pauseWorkflow({
            analytics: monitoringAnalytics(),
            workflowId: pauseId,
          })
        );
      })
    );
  };

  // Reverse of a skip: the previous pair rises from under the stack, out to the
  // sides, then into the front, while the current pair sinks back into the deck.
  const goBack = () => {
    if (animatingRef.current || index === 0) return;
    animatingRef.current = true;
    setResolving(false);
    setLeaveDir("skip");
    setLeaveStage("in");
    setThrowing(false);
    setAnimating(true);
    setBackRising(true);
    setIndex(index - 1);
    window.setTimeout(() => setBackRising(false), SKIP_STAGE_MS);
    window.setTimeout(() => {
      animatingRef.current = false;
      setAnimating(false);
      setBackRising(false);
    }, DEAL_MS);
  };

  const previewPair = pendingPairs[0];

  const formatRelativeTime = useFormatRelativeTime();
  const detectionStale = useDeadlinePassed(
    detectionRun != null && detectionRun.completedAt == null
      ? detectionRun.startedAt + IN_PROGRESS_MS
      : null
  );

  // The swipe handlers set `animatingRef` synchronously to guard against a
  // second swipe before re-render; mirror `animating` here so the open-reset
  // below (which only calls `setAnimating`) also clears the ref.
  useEffect(() => {
    animatingRef.current = animating;
  }, [animating]);

  if (
    pairsQuery.type !== "complete" ||
    reviewsQuery.type !== "complete" ||
    detectionRunQuery.type !== "complete"
  ) {
    return <PanelSkeleton />;
  }

  const inProgress =
    detectionRun != null && detectionRun.completedAt == null && !detectionStale;
  // A running re-check nulls completedAt; hold the last value so the tooltip
  // stays put until the new check lands.
  if (
    detectionRun?.completedAt != null &&
    detectionRun.completedAt !== lastCompletedAt
  ) {
    setLastCompletedAt(detectionRun.completedAt);
  }
  const lastCheckedLabel =
    lastCompletedAt != null
      ? `Last checked ${formatRelativeTime(lastCompletedAt)}`
      : "Not checked yet";

  // Snapshot the pending pairs and reset the swipe state the moment the deck
  // opens, during render so the first painted frame already has cards — an
  // effect would run after paint and flash an empty deck.
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) {
      setQueue(pendingPairs);
      setIndex(0);
      setResolving(false);
      setAnimating(false);
      setLeaveDir("skip");
      setLeaveStage("in");
      setThrowing(false);
      setBackRising(false);
      setEntered(false);
    }
  }

  return (
    <>
      <SummaryCard
        title="Duplicate workflows"
        value={pendingPairs.length}
        valueLabel={
          pendingPairs.length === 1 ? "pair to review" : "pairs to review"
        }
        onOpen={pendingPairs.length > 0 ? openDeck : undefined}
        accessory={<PreviewDeck pair={previewPair} />}
        interactiveTitleAccessory
        titleAccessory={
          <Tooltip content={lastCheckedLabel}>
            <RefreshButton
              disabled={inProgress}
              onClick={() => {
                recordInteraction("workflows.duplicate_refresh");
                z.mutate(sharedMutators.refreshDuplicateDetection({}));
              }}
              aria-label="Refresh duplicate workflows"
            >
              <RefreshSpin spinning={inProgress}>
                <Icon name="refresh-cw" size={12} />
              </RefreshSpin>
            </RefreshButton>
          </Tooltip>
        }
      />

      <Modal open={open} onClose={close} style={DETAIL_MODAL_STYLE}>
        <ModalHeader>
          <ModalTitle icon="heart-handshake" title="Duplicate workflows" />
          <ModalHeaderRight>
            <Button size="l" variant="secondary" icon="x" onClick={close} />
          </ModalHeaderRight>
        </ModalHeader>

        <ModalBody>
          {showDone ? (
            <DoneState>
              <DoneIcon>
                <Icon name="check" size={32} color="currentColor" />
              </DoneIcon>
              <Text variant="text-regular-md">All caught up</Text>
              <Text variant="text-small" color="var(--text-secondary)">
                {total > 0
                  ? `You reviewed ${total} ${total === 1 ? "pair" : "pairs"}. Nice work.`
                  : "No duplicate pairs left to review."}
              </Text>
            </DoneState>
          ) : (
            <Stage>
              <ProgressTrack>
                <ProgressFill
                  style={{
                    width: `${(Math.min(index, total) / total) * 100}%`,
                  }}
                />
              </ProgressTrack>

              <Prompt>
                {current ? (
                  <>
                    <Text variant="display-md">
                      {resolving
                        ? "Which one should we keep?"
                        : "Are these the same?"}
                    </Text>
                    <Text variant="text-small" color="var(--text-secondary)">
                      {resolving
                        ? "The other will be paused."
                        : (current.reason ?? "")}
                    </Text>
                  </>
                ) : null}
              </Prompt>

              <Deck>
                {[-1, 0, 1, 2].flatMap((offset) => {
                  const pair = queue[index + offset];
                  if (!pair) return [];
                  const active = offset === 0;
                  const interactive = active && resolving;
                  const recede = resolving && offset > 0;
                  const opts = {
                    entered: active ? entered : true,
                    resolving: interactive,
                    backRising: active && backRising,
                    recede,
                    leaveDir,
                    leaveStage,
                  };
                  const cardZ =
                    offset < 0
                      ? leaveDir === "skip" && leaveStage === "in"
                        ? 1
                        : 40
                      : 30 - offset * 10;
                  // While a skip throws the old pair out, hold the incoming
                  // cards a beat so they settle into focus afterward.
                  const transitionDelay =
                    throwing && offset >= 0 ? "120ms" : undefined;
                  return (["left", "right"] as const).map((side) => {
                    const workflow =
                      side === "left" ? pair.workflowA : pair.workflowB;
                    const purpose =
                      side === "left"
                        ? pair.workflowAPurpose
                        : pair.workflowBPurpose;
                    const canKeep =
                      interactive && (side === "left" ? canPauseB : canPauseA);
                    const cardClass = canKeep
                      ? "selectable"
                      : recede
                        ? "receded"
                        : undefined;
                    return (
                      <Card
                        key={`${pairKey(pair.workflowAId, pair.workflowBId)}:${side}`}
                        className={cardClass}
                        style={{
                          zIndex: side === "left" ? cardZ + 1 : cardZ,
                          ...cardPose(offset, side, opts),
                          transitionDelay,
                        }}
                        onMouseDown={canKeep ? () => playClick() : undefined}
                        onClick={canKeep ? () => keep(workflow.id) : undefined}
                      >
                        <CardContent workflow={workflow} purpose={purpose} />
                      </Card>
                    );
                  });
                })}
              </Deck>

              <Decisions>
                {!current ? null : resolving ? (
                  <SoftButton
                    size="l"
                    variant="secondary"
                    onClick={() => setResolving(false)}
                  >
                    Back
                  </SoftButton>
                ) : (
                  <>
                    <SoftButton
                      size="l"
                      variant="secondary"
                      icon="arrow-left"
                      disabled={index === 0}
                      onClick={goBack}
                    />
                    <VerdictButton
                      size="l"
                      variant="primary"
                      onClick={() => decide("different")}
                    >
                      Keep both
                    </VerdictButton>
                    <Tooltip
                      content="You don’t have permission to pause these workflows"
                      hidden={canPauseA || canPauseB}
                    >
                      <VerdictButton
                        size="l"
                        variant="primary"
                        disabled={!canPauseA && !canPauseB}
                        onClick={() => decide("same")}
                      >
                        Pick one
                      </VerdictButton>
                    </Tooltip>
                    <SoftButton
                      size="l"
                      variant="secondary"
                      icon="arrow-right"
                      onClick={() => decide("skip")}
                    />
                  </>
                )}
              </Decisions>
            </Stage>
          )}
        </ModalBody>

        <ModalFooter>
          <FooterLeft>
            {!showDone ? (
              <Text variant="text-small" color="var(--text-secondary)">
                {remaining} left
              </Text>
            ) : null}
          </FooterLeft>
          <Button size="l" variant="primary" onClick={close}>
            Done
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
